import {Box, Checkbox, Table, TableData} from '@viptech/react-components'
import moment from 'moment'
import {useSnackbar} from 'notistack'
import {useEffect, useState} from 'react'
import {useLocation} from 'react-router-dom'
import {ValidateUserPermissions} from 'src/common/utils/ValidatePermissions'
import AppAddOrOptionsButton from 'src/components/AppAddOrOptionsButton'
import {ConfirmationModal} from 'src/components/ConfirmationModal/ConfirmationModal'
import {DataTablePagination} from 'src/components/data-table-pagination'
import RowHover from 'src/components/RowHover/RowHover'
import {TableHeaderColumn, TableHeaderRow} from 'src/components/table/components/tableHeader/TableHeaderBase'
import useDebounce from 'src/hooks/useDebounce'
import {useApp} from 'src/layouts/main-layout/contexts/AppContext'
import api from 'src/services/api'
import {Page} from 'src/types'
import handleErrorWithSnackbar from 'src/utilities/handleErrorWithSnackbar'
import {FacialListDTO} from '../../../../common/models/facial-recognition/FacialListDTO'
import AppReloadButton from '../../../../components/appReloadButton/AppReloadButton'
import CheckboxTable from '../../../../components/CheckboxTable'
import {DropdownItem} from '../../../../components/dropdown/DropdownProps'
import LinkWithAction from '../../../../components/LinkWithAction'
import {SelectWithSearchComponent} from '../../../../components/selectWithSearchComponent/SelectWithSearchComponent'
import TopBar from '../../../../components/top-bar/TopBar'
import useFacialListItems from '../../hooks/useFacialListItems'
import ListOptionsMenu from '../listsComponents/ListOptionsMenu'
import {ListsContextMenu} from '../listsComponents/ListsContextMenu'
import CreateFaceModal from './CreateFaceModal'
import UpdateFaceModal from './UpdateFaceModal'

const columns = [
  {
    id: 'checkbox',
    name: '',
  },
  {
    id: 'name',
    name: 'Nome Cadastrado',
  },
  {
    id: 'date',
    name: 'Data e hora',
  },
  {
    id: 'cropImage',
    name: 'Imagem',
  },
  {
    id: 'actions',
    name: '',
  },
]

const searchFilterData = [{id: '1', label: 'Nome Cadastrado'}]

function FacialListTable() {
  const {enqueueSnackbar} = useSnackbar()
  const [listSearch, setListSearch] = useState('')
  const listSearchDebounced = useDebounce(listSearch, 500)
  const hasFaceDeletePermission = ValidateUserPermissions('face:delete')
  const [isOpen, setIsOpen] = useState(false)
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false)
  const [callback, setCallback] = useState<() => void>(() => {})
  const [menuEl, setMenuEl] = useState<null | HTMLElement>(null)

  const [page, setPage] = useState<Page>({
    page: 1,
    pageSize: 20,
  })

  const {setSubMenuTitleName} = useApp()

  const setCurrentPage = (page: number): void => {
    setPage((prev: Page) => ({...prev, page}))
  }

  const setPageSize = (pageSize: number): void => {
    setPage((prev: Page) => ({...prev, pageSize}))
  }

  const {search} = useLocation()
  const listId = +search.split('=')[1]

  const {listItems, isLoading, totalItems, getFaceListItems} = useFacialListItems({listId, filter: listSearchDebounced, page})

  const tableControl = {
    pageIndex: page.page,
    pageSize: page.pageSize,
    total: totalItems,
    setPageSize: setPageSize,
    setPageIndex: setCurrentPage,
    nextPage: () => {
      setCurrentPage(page.page + 1)
    },
    previousPage: () => {
      setCurrentPage(page.page - 1)
    },
  }

  useEffect(() => {
    getNameOfList()
  }, [])

  useEffect(() => {
    getFaceListItems()
  }, [listSearchDebounced, page])

  async function getNameOfList() {
    const listInfos = await api.facialRecognition.getList({filter: {ids: [listId]}})
    setSubMenuTitleName(listInfos.data.data.entities[0].name)
  }

  async function handleDeleteFace(id: string) {
    try {
      await api.facialRecognition.deleteFacial({face_id: +id, list_id: listId})
      enqueueSnackbar('Face deletada com sucesso', {variant: 'success'})
      getFaceListItems()
    } catch (err) {
      handleErrorWithSnackbar(enqueueSnackbar, err, 'Erro ao deletar face')
    }
  }

  async function handleConfirmDelete(id: string) {
    await handleDeleteFace(id)
    setIsConfirmationOpen(false)
  }

  async function handleConfirmDeleteMultipleFaces() {
    selectedListsIds.forEach((face) => handleDeleteFace(String(face)))
    setIsConfirmationOpen(false)
  }

  async function handleClose(shouldUpdateTable: boolean) {
    setIsOpen(false)
    setIsEditing(false)
    if (shouldUpdateTable) getFaceListItems()
  }

  const [showSearchSelected, setShowSearchSelected] = useState<DropdownItem>({
    id: '',
    label: '',
  })

  const [isEditing, setIsEditing] = useState(false)
  const [imageKeySelected, setImageKeySelected] = useState<FacialListDTO | null>(null)
  const [selectedListsIds, setSelectedListsIds] = useState<number[]>([])

  const onSelectAll = () => {
    if (selectedListsIds.length) return setSelectedListsIds([])

    const allIds = listItems.map((row) => row.id)
    setSelectedListsIds(allIds)
  }

  const customHeader = () => {
    return (
      <thead>
        <TableHeaderRow>
          {columns.map((col) =>
            col.id === 'checkbox' ? (
              <TableHeaderColumn>
                <div style={{paddingLeft: '10px'}}>
                  <Checkbox id='check' checked={selectedListsIds.length ? listItems.length === selectedListsIds.length : false} onChange={onSelectAll} />
                </div>
              </TableHeaderColumn>
            ) : (
              <TableHeaderColumn>{col.name}</TableHeaderColumn>
            ),
          )}
        </TableHeaderRow>
      </thead>
    )
  }
  return (
    <Box borderRadius='8px' align='center' direction='column' backgroundColor='white'>
      <ConfirmationModal
        isOpen={isConfirmationOpen}
        onClose={() => setIsConfirmationOpen(false)}
        title='Deletar face'
        content='Deseja realmente deletar esta face? Esta ação não pode ser revertida.'
        buttonsContent={[
          {
            label: 'Cancelar',
            onClick: () => setIsConfirmationOpen(false),
            variant: 'outlined',
            color: 'black',
            hoverColor: 'darkgray',
          },
          {
            label: 'Excluir',
            onClick: () => callback(),
            variant: 'contained',
            dangerAction: true,
          },
        ]}
      />
      <TopBar>
        <Box display='flex' direction='row' justifyContent='space-between'>
          <Box display='flex' columnGap='16px'>
            <AppReloadButton loading={isLoading} onClick={() => getFaceListItems()} />
            <SelectWithSearchComponent
              hideAdvancedFilters
              searchBy={searchFilterData}
              setSearchSelected={setShowSearchSelected}
              searchSelected={showSearchSelected}
              inputValue={listSearch}
              inputSearch={setListSearch}
              widthOnSelect={'280px'}
            />
          </Box>
          <AppAddOrOptionsButton
            text='Vincular uma face'
            onClickAdd={() => setIsOpen(true)}
            showOptions={selectedListsIds.length > 1}
            onClickOptions={(e) => setMenuEl(e.currentTarget)}
            sx={{width: selectedListsIds.length > 1 ? '110px' : '200px'}}
          />
        </Box>
      </TopBar>

      <CreateFaceModal isOpen={isOpen} closeModal={handleClose} listId={listId!} />
      {imageKeySelected && isEditing && <UpdateFaceModal faceSelected={imageKeySelected} isOpen={isEditing} closeModal={handleClose} />}

      <Box height='79vh' overflow='auto' padding='0 20px' display='flex' direction='column'>
        <Box flex={1} display={'flex'}>
          <Table columns={columns} isLoading={isLoading} tableHeaderChildren={customHeader()}>
            {listItems.map((row) => {
              const key = `${row.id}`
              return (
                <RowHover key={key}>
                  <TableData paddingLeft='10px'>
                    <CheckboxTable id={row.id} selectedIds={selectedListsIds} setSelectedIds={setSelectedListsIds} />
                  </TableData>

                  <TableData>
                    <LinkWithAction
                      onClick={() => {
                        setImageKeySelected(row)
                        setIsEditing(true)
                      }}>
                      {row.name}
                    </LinkWithAction>
                  </TableData>
                  <TableData>{moment(row.createdAt).format('DD/MM/YYYY - HH:mm')}</TableData>
                  <TableData>
                    <Box display='flex' align='center' justifyContent='flex-start' padding='1px' width='200px' height='50px'>
                      {row.faceImages && row.faceImages[0] && row.faceImages[0].aws_cropped_image_key && (
                        <img
                          key={row.id}
                          src={`${process.env.REACT_APP_TEMPLATE_IMAGES_BASE_URL}/${row.faceImages[0].aws_cropped_image_key}`}
                          alt={`Imagem não encontrada`}
                          style={{width: '45px', height: '50px', objectFit: 'contain'}}
                        />
                      )}
                    </Box>
                  </TableData>

                  <TableData>
                    <ListsContextMenu
                      contextInsideList
                      hasDeletePermission={hasFaceDeletePermission}
                      rowId={`${row.id}`}
                      onClick={(event, rowListId) => {
                        if (event === 'delete') {
                          setIsConfirmationOpen(true)
                          setCallback(() => () => handleConfirmDelete(rowListId))
                        }
                        if (event === 'edit') {
                          setImageKeySelected(row)
                          setIsEditing(true)
                        }
                      }}
                    />
                  </TableData>
                </RowHover>
              )
            })}
          </Table>
        </Box>

        {!isLoading && listItems.length === 0 ? (
          <Box display='flex' align='center' justify='center' height='72vh'>
            Nenhum registro encontrado
          </Box>
        ) : (
          <Box display='flex' align='flex-start' justify='flex-end' margin='15px 0'>
            <DataTablePagination table={tableControl} />
          </Box>
        )}
      </Box>
      <ListOptionsMenu open={Boolean(menuEl)} anchorEl={menuEl} onClose={() => setMenuEl(null)} onDelete={handleConfirmDeleteMultipleFaces} />
    </Box>
  )
}

export default FacialListTable
